import type { CashDrawerApi } from "../../api/CashDrawerApi";
import type { CashDrawerAddCashRequest } from "../../generated/dto/CashDrawerAddCashRequest";
import type { CashDrawerChangeFetchOptions } from "../../generated/dto/CashDrawerChangeFetchOptions";
import type { CashDrawerChangeRef } from "../../generated/dto/CashDrawerChangeRef";
import type { CashDrawerChangeStaff } from "../../generated/dto/CashDrawerChangeStaff";
import type { CashDrawerDayChangesRequest } from "../../generated/dto/CashDrawerDayChangesRequest";
import type { CashDrawerFetchOptions } from "../../generated/dto/CashDrawerFetchOptions";
import type { CashDrawerOpenDayRequest } from "../../generated/dto/CashDrawerOpenDayRequest";
import type { CashDrawerRef } from "../../generated/dto/CashDrawerRef";
import type { CashDrawerRemoveCashRequest } from "../../generated/dto/CashDrawerRemoveCashRequest";
import type { CashDrawerSaveCountRequest } from "../../generated/dto/CashDrawerSaveCountRequest";
import type { CashDrawerSaveStartingCountRequest } from "../../generated/dto/CashDrawerSaveStartingCountRequest";
import type { CashDrawerSearchParams } from "../../generated/dto/CashDrawerSearchParams";
import type { CashDrawerStaff } from "../../generated/dto/CashDrawerStaff";
import type { CashDrawerStaffSearchResult } from "../../generated/dto/CashDrawerStaffSearchResult";
import type { CashDrawerTotalFetchOptions } from "../../generated/dto/CashDrawerTotalFetchOptions";
import type { CashDrawerTotalRef } from "../../generated/dto/CashDrawerTotalRef";
import type { CashDrawerTotalStaff } from "../../generated/dto/CashDrawerTotalStaff";
import type { CreateCashDrawer } from "../../generated/dto/CreateCashDrawer";
import type { DeleteCashDrawerChangeStaff } from "../../generated/dto/DeleteCashDrawerChangeStaff";
import type { DeleteCashDrawerStaff } from "../../generated/dto/DeleteCashDrawerStaff";
import type { DeleteCashDrawerTotalStaff } from "../../generated/dto/DeleteCashDrawerTotalStaff";
import type { PrinterFetchOptions } from "../../generated/dto/PrinterFetchOptions";
import type { PrinterStaff } from "../../generated/dto/PrinterStaff";
import type { UpdateCashDrawer } from "../../generated/dto/UpdateCashDrawer";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class CashDrawerFrontendService {
  constructor(private readonly api: CashDrawerApi) {}
  async getCashDrawerStaff(id: string, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff> {
    return this.api.getCashDrawerStaff(id, options)
  }
  async getCashDrawerChangeStaff(id: number, options?: CashDrawerChangeFetchOptions): Promise<CashDrawerChangeStaff> {
    return this.api.getCashDrawerChangeStaff(id, options)
  }
  async getCashDrawerTotalStaff(id: number, options?: CashDrawerTotalFetchOptions): Promise<CashDrawerTotalStaff> {
    return this.api.getCashDrawerTotalStaff(id, options)
  }
  async createCashDrawerStaff(source: CreateCashDrawer): Promise<CashDrawerStaff> {
    return this.api.createCashDrawerStaff(source)
  }
  async updateCashDrawerStaff(source: UpdateCashDrawer): Promise<CashDrawerStaff> {
    return this.api.updateCashDrawerStaff(source)
  }
  async deleteCashDrawerStaff(source: DeleteCashDrawerStaff): Promise<void> {
    return this.api.deleteCashDrawerStaff(source)
  }
  async deleteCashDrawerChangeStaff(source: DeleteCashDrawerChangeStaff): Promise<void> {
    return this.api.deleteCashDrawerChangeStaff(source)
  }
  async deleteCashDrawerTotalStaff(source: DeleteCashDrawerTotalStaff): Promise<void> {
    return this.api.deleteCashDrawerTotalStaff(source)
  }
  async searchCashDrawerStaff(params: CashDrawerSearchParams, page: number, perPage: number): Promise<PaginationResult<CashDrawerStaffSearchResult>> {
    return this.api.searchCashDrawerStaff(params, page, perPage)
  }
  async searchCashDrawerStaffFull(params: CashDrawerSearchParams, page: number, perPage: number, options?: CashDrawerFetchOptions): Promise<PaginationResult<CashDrawerStaff>> {
    return this.api.searchCashDrawerStaffFull(params, page, perPage, options)
  }
  async getCashDrawerStaffPrinter(id: string, options?: PrinterFetchOptions): Promise<PrinterStaff | null> {
    return this.api.getCashDrawerStaffPrinter(id, options)
  }
  async getCashDrawerStaffChanges(id: string, options?: CashDrawerChangeFetchOptions): Promise<Array<CashDrawerChangeStaff>> {
    return this.api.getCashDrawerStaffChanges(id, options)
  }
  async getCashDrawerStaffTotals(id: string, options?: CashDrawerTotalFetchOptions): Promise<Array<CashDrawerTotalStaff>> {
    return this.api.getCashDrawerStaffTotals(id, options)
  }
  async getCashDrawerChangeStaffCashDrawer(id: number, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff | null> {
    return this.api.getCashDrawerChangeStaffCashDrawer(id, options)
  }
  async getCashDrawerTotalStaffCashDrawer(id: number, options?: CashDrawerFetchOptions): Promise<CashDrawerStaff | null> {
    return this.api.getCashDrawerTotalStaffCashDrawer(id, options)
  }
  async addCashStaff(request: CashDrawerAddCashRequest): Promise<CashDrawerChangeRef> {
    return this.api.addCashStaff(request)
  }
  async dailyTotalsStaff(date: Date): Promise<Array<CashDrawerTotalRef>> {
    return this.api.dailyTotalsStaff(date)
  }
  async dayChangesStaff(request: CashDrawerDayChangesRequest): Promise<Array<CashDrawerChangeRef>> {
    return this.api.dayChangesStaff(request)
  }
  async finalizeStaff(total: CashDrawerTotalRef): Promise<void> {
    return this.api.finalizeStaff(total)
  }
  async openDayStaff(request: CashDrawerOpenDayRequest): Promise<CashDrawerTotalRef> {
    return this.api.openDayStaff(request)
  }
  async popStaff(drawer: CashDrawerRef): Promise<void> {
    return this.api.popStaff(drawer)
  }
  async printTestStaff(drawer: CashDrawerRef): Promise<string> {
    return this.api.printTestStaff(drawer)
  }
  async removeCashStaff(request: CashDrawerRemoveCashRequest): Promise<CashDrawerChangeRef> {
    return this.api.removeCashStaff(request)
  }
  async saveCountStaff(request: CashDrawerSaveCountRequest): Promise<void> {
    return this.api.saveCountStaff(request)
  }
  async saveStartingCountStaff(request: CashDrawerSaveStartingCountRequest): Promise<void> {
    return this.api.saveStartingCountStaff(request)
  }
}
